import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const LOGO = `${process.env.PUBLIC_URL}/1.png`;
const BACKGROUND_IMAGE = `${process.env.PUBLIC_URL}/background.png`;

const API_BASE = process.env.REACT_APP_API_URL || (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.insightos.co.uk'
    : 'https://api.insightos.co.uk'
);

const RESULT_STYLES = {
  accepted: {
    Icon: CheckCircleIcon,
    eyebrow: 'Shift accepted',
    title: 'Your cover shift is confirmed',
    accent: 'text-emerald-300',
    iconBackground: 'bg-emerald-500/20',
  },
  declined: {
    Icon: XCircleIcon,
    eyebrow: 'Shift declined',
    title: 'Your response has been recorded',
    accent: 'text-rose-300',
    iconBackground: 'bg-rose-500/20',
  },
  expired: {
    Icon: ClockIcon,
    eyebrow: 'Link expired',
    title: 'This request is no longer active',
    accent: 'text-amber-300',
    iconBackground: 'bg-amber-500/20',
  },
  default: {
    Icon: ExclamationTriangleIcon,
    eyebrow: 'Unable to respond',
    title: 'We could not record your response',
    accent: 'text-red-300',
    iconBackground: 'bg-red-500/20',
  },
};

// ─── Animated orbs (same as 404/503 pages) ──────────────────────────────────
const orbConfigs = [
  {
    className: 'w-8 h-8 bg-blue-400 opacity-70',
    animate: { x: [0, 200, -100, 150, 0], y: [0, -150, 100, -50, 0], scale: [1, 1.2, 0.8, 1.5, 1] },
    transition: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
    style: { top: '20%', left: '10%' },
  },
  {
    className: 'w-6 h-6 bg-blue-500 opacity-60',
    animate: { x: [0, -180, 120, -80, 0], y: [0, 120, -80, 160, 0], scale: [1, 0.7, 1.3, 0.9, 1] },
    transition: { duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 },
    style: { top: '60%', right: '15%' },
  },
  {
    className: 'w-10 h-10 bg-blue-300 opacity-50',
    animate: { x: [0, 100, -200, 50, 0], y: [0, -100, 80, -120, 0], scale: [1, 1.1, 0.6, 1.4, 1] },
    transition: { duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 },
    style: { bottom: '20%', left: '20%' },
  },
  {
    className: 'w-7 h-7 bg-purple-400 opacity-70',
    animate: { x: [0, -150, 80, -120, 0], y: [0, 100, -140, 60, 0], scale: [1, 1.3, 0.8, 1.1, 1] },
    transition: { duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 0.5 },
    style: { top: '40%', right: '20%' },
  },
  {
    className: 'w-9 h-9 bg-purple-500 opacity-60',
    animate: { x: [0, 160, -90, 200, 0], y: [0, -80, 120, -160, 0], scale: [1, 0.9, 1.2, 0.7, 1] },
    transition: { duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 1.5 },
    style: { bottom: '30%', right: '10%' },
  },
  {
    className: 'w-5 h-5 bg-purple-300 opacity-80',
    animate: { x: [0, -120, 160, -80, 0], y: [0, -60, 90, -110, 0], scale: [1, 1.4, 0.6, 1.2, 1] },
    transition: { duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 3 },
    style: { top: '10%', right: '30%' },
  },
  {
    className: 'w-4 h-4 bg-blue-200 opacity-40',
    animate: { x: [0, 80, -60, 100, 0], y: [0, -40, 70, -90, 0], scale: [1, 0.8, 1.1, 0.9, 1] },
    transition: { duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 2.5 },
    style: { bottom: '10%', left: '40%' },
  },
  {
    className: 'w-3 h-3 bg-purple-200 opacity-50',
    animate: { x: [0, -70, 90, -40, 0], y: [0, 50, -80, 110, 0], scale: [1, 1.2, 0.7, 1.3, 1] },
    transition: { duration: 8.5, repeat: Infinity, ease: 'easeInOut', delay: 1.8 },
    style: { top: '70%', left: '60%' },
  },
];

export default function StandbyResponse() {
  const { token, action } = useParams();
  const isAccepting = action === '1';
  const isValid = /^[a-f0-9]{32}$/i.test(token || '') && ['1', '2'].includes(action);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(
    isValid ? null : { status: 'invalid', message: 'This standby response link is invalid.' }
  );

  async function submitResponse() {
    if (!isValid || submitting || result) return;

    setSubmitting(true);
    try {
      const response = await fetch(`${API_BASE}/api/standby/${token}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const payload = await response.json().catch(() => ({}));
      setResult({
        status: payload.status || (response.ok ? (isAccepting ? 'accepted' : 'declined') : 'error'),
        message: payload.message || 'We could not record your response. Please try again.',
      });
    } catch (error) {
      setResult({
        status: 'error',
        message: 'We could not reach InsightOS. Check your connection and try again.',
        retryable: true,
      });
    } finally {
      setSubmitting(false);
    }
  }

  function retry() {
    setResult(null);
  }

  const resultStyle = result
    ? RESULT_STYLES[result.status] || RESULT_STYLES.default
    : null;
  const ResultIcon = resultStyle?.Icon;

  return (
    <div
      className="min-h-screen w-full bg-slate-900 text-white relative overflow-hidden"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(17, 65, 175, 0.75), rgba(51, 3, 103, 0.9)), url(${BACKGROUND_IMAGE})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Animated Orbs */}
      {orbConfigs.map((orb, index) => (
        <motion.div
          key={index}
          className={`absolute rounded-full ${orb.className}`}
          animate={orb.animate}
          transition={orb.transition}
          style={orb.style}
        />
      ))}

      {/* Content */}
      <div className="relative z-10 mx-auto flex min-h-screen max-w-4xl flex-col px-6 py-8">
        {/* Header with Logo */}
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="InsightOS" className="h-10 w-10" />
          <div className="text-base font-semibold tracking-wide text-slate-100">InsightOS</div>
        </div>

        {/* Main Card */}
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-2xl rounded-3xl bg-white/10 p-8 shadow-2xl backdrop-blur-md sm:p-12">
            {result ? (
              // ── Result state ──
              <div className="text-center" aria-live="polite">
                <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-2xl ${resultStyle.iconBackground} ${resultStyle.accent}`}>
                  <ResultIcon className="h-10 w-10" aria-hidden="true" />
                </div>
                <p className={`mt-6 text-sm font-bold uppercase tracking-wider ${resultStyle.accent}`}>
                  {resultStyle.eyebrow}
                </p>
                <h1 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
                  {resultStyle.title}
                </h1>
                <p className="mt-4 text-base leading-7 text-slate-200/80">{result.message}</p>
                {result.retryable && (
                  <button
                    type="button"
                    onClick={retry}
                    className="mt-8 inline-flex min-h-12 items-center justify-center rounded-xl bg-white/10 px-6 text-sm font-bold text-white hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                  >
                    Try again
                  </button>
                )}
              </div>
            ) : (
              // ── Confirmation state ──
              <div className="text-center">
                <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-2xl ${isAccepting ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`}>
                  {isAccepting
                    ? <CheckCircleIcon className="h-10 w-10" aria-hidden="true" />
                    : <XCircleIcon className="h-10 w-10" aria-hidden="true" />}
                </div>
                <p className={`mt-6 text-sm font-bold uppercase tracking-wider ${isAccepting ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {isAccepting ? 'Accept standby shift' : 'Decline standby shift'}
                </p>
                <h1 className="mt-3 text-3xl font-bold text-white sm:text-4xl">
                  Confirm your response
                </h1>
                <p className="mt-4 text-base leading-7 text-slate-200/80">
                  {isAccepting
                    ? 'Confirm that you are available and want to cover this shift.'
                    : 'Confirm that you are unavailable. The request will move to the next standby volunteer.'}
                </p>
                <button
                  type="button"
                  onClick={submitResponse}
                  disabled={submitting}
                  className={`mt-8 inline-flex min-h-12 w-full items-center justify-center rounded-xl px-6 text-sm font-bold text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-wait disabled:opacity-60 sm:w-auto ${
                    isAccepting
                      ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500'
                      : 'bg-rose-600 hover:bg-rose-700 focus:ring-rose-500'
                  }`}
                >
                  {submitting ? 'Recording response…' : isAccepting ? 'Confirm acceptance' : 'Confirm decline'}
                </button>
              </div>
            )}
          </div>
        </div>

        <footer className="mt-8 text-center text-xs text-slate-400/70">
          Secure shift response powered by InsightOS
        </footer>
      </div>
    </div>
  );
}